'use client';

import { trpc } from '@/lib/trpc';

/**
 * Hooks for public location lookups with cascading selection.
 * Designed for registration flows where users select Country -> State -> Ride.
 *
 * Heavy caching - this data rarely changes and gets hit hard during peak registration.
 */

// 1 hour stale time, 2 hour garbage collection for location data
const LOCATION_CACHE_MS = 1000 * 60 * 60;
// 15 min stale time, 1 hour garbage collection for rides (changes more often)
const RIDES_CACHE_MS = 1000 * 60 * 15;
// 1 hour stale time for vehicle data (rarely changes)
const VEHICLE_CACHE_MS = 1000 * 60 * 60;

/**
 * Hook to fetch all active countries.
 * Heavily cached since country list rarely changes.
 */
export function useCountries() {
  return trpc.publicLookups.countries.useQuery(undefined, {
    staleTime: LOCATION_CACHE_MS,
    gcTime: LOCATION_CACHE_MS * 2,
  });
}

/**
 * Hook to fetch states/regions for a country.
 * Only fetches when countryCode is provided.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "AU")
 */
export function useStatesByCountry(countryCode: string | undefined) {
  return trpc.publicLookups.statesByCountry.useQuery(
    { countryCode: countryCode! },
    {
      enabled: !!countryCode,
      staleTime: LOCATION_CACHE_MS,
      gcTime: LOCATION_CACHE_MS * 2,
    },
  );
}

/**
 * Hook to fetch states that have at least one active ride.
 * Used by registration flow to avoid showing empty states.
 */
export function useStatesWithRides(countryCode: string | undefined) {
  return trpc.publicLookups.statesWithRides.useQuery(
    { countryCode: countryCode! },
    {
      enabled: !!countryCode,
      staleTime: RIDES_CACHE_MS,
      gcTime: RIDES_CACHE_MS * 4,
    },
  );
}

/**
 * Hook to fetch rides (events) for a location.
 * Implements cascading logic - only fetches when location is complete.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "AU")
 * @param stateCode - State/region code (e.g., "NSW"), optional
 * @param hasStates - Whether the selected country has states/regions
 */
export function useRidesByLocation(
  countryCode: string | undefined,
  stateCode: string | undefined,
  hasStates: boolean,
) {
  // Only fetch when: country selected AND (no states OR state selected)
  const enabled = !!countryCode && (!hasStates || !!stateCode);

  return trpc.publicLookups.ridesByLocation.useQuery(
    { countryCode: countryCode!, stateCode: stateCode || undefined },
    {
      enabled,
      staleTime: RIDES_CACHE_MS,
      gcTime: RIDES_CACHE_MS * 4,
    },
  );
}

/**
 * Hook exposing the active campaign's registration-closed state.
 *
 * Closed campaigns can stay publicly visible (events.show_closed_campaign_events),
 * so registration UIs must read this flag explicitly instead of inferring the
 * state from empty ride lists. `closedMessage` is the optional admin-configured
 * copy from campaignSettings.
 */
export function useActiveCampaignClosed() {
  const { data: activeCampaigns } = trpc.publicSearch.campaigns.useQuery(undefined, {
    staleTime: RIDES_CACHE_MS,
    refetchOnWindowFocus: false,
  });
  const firstCampaign = activeCampaigns?.[0];

  return {
    isCampaignClosed: firstCampaign?.campaignClosed === true,
    closedMessage:
      (firstCampaign?.campaignSettings as { closedMessage?: string } | null | undefined)
        ?.closedMessage ?? null,
  };
}

/**
 * Hook to fetch distinct vehicle makes for a type.
 * Only fetches when type is provided.
 *
 * @param type - Vehicle type ('motorcycle' or 'car')
 */
export function useVehicleMakes(type: 'motorcycle' | 'car' | null) {
  return trpc.publicLookups.vehicleMakes.useQuery(
    { type: type! },
    {
      enabled: !!type,
      staleTime: VEHICLE_CACHE_MS,
      gcTime: VEHICLE_CACHE_MS * 2,
    },
  );
}

/**
 * Hook to fetch vehicle models for a type and make.
 * Implements cascading logic - only fetches when type and make are selected.
 *
 * @param type - Vehicle type ('motorcycle' or 'car')
 * @param make - Vehicle make (e.g., "Triumph")
 */
export function useVehicleModels(type: 'motorcycle' | 'car' | null, make: string | null) {
  return trpc.publicLookups.vehicleModels.useQuery(
    { type: type!, make: make! },
    {
      enabled: !!type && !!make,
      staleTime: VEHICLE_CACHE_MS,
      gcTime: VEHICLE_CACHE_MS * 2,
    },
  );
}
